import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { dashboardCardStyles } from './sz-dashboard-card.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-quick-actions-card': SzQuickActionsCard;
  }
}

export interface IQuickAction {
  label: string;
  icon?: string;
  primary?: boolean;
  url?: string;
}

@customElement('sz-quick-actions-card')
export class SzQuickActionsCard extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 800px;">
      <sz-quick-actions-card
        .actions=${[
          { label: 'Deploy Service', icon: 'lucide:Plus', primary: true },
          { label: 'View All Services', icon: 'lucide:Server' },
          { label: 'Manage Domains', icon: 'lucide:Globe' },
        ]}
      ></sz-quick-actions-card>
    </div>
  `;

  public static demoGroups = ['Dashboard'];

  @property({ type: Array })
  public accessor actions: IQuickAction[] = [];

  public static styles = [
    cssManager.defaultStyles,
    dashboardCardStyles,
    css`
      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--dees-spacing-sm);
      }

      @media (max-width: 460px) {
        .actions {
          display: grid;
          grid-template-columns: 1fr;
        }

        dees-button {
          width: 100%;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-tile>
        <div slot="header" class="card-header">
          <div class="header-copy">
            <div class="card-title">Quick Actions</div>
            <div class="card-subtitle">Common platform tasks</div>
          </div>
          <dees-icon icon="lucide:Zap" .iconSize=${18}></dees-icon>
        </div>

        <div class="card-content">
          ${this.actions.length > 0
            ? html`
                <div class="actions">
                  ${this.actions.map(
                    (action) => html`
                      <dees-button
                        .type=${action.primary ? 'accent' : 'secondary'}
                        .text=${action.label}
                        .icon=${this.normalizeIcon(action.icon)}
                        @clicked=${() => this.handleActionClick(action)}
                      ></dees-button>
                    `
                  )}
                </div>
              `
            : html`<div class="empty-state">No quick actions available.</div>`}
        </div>
      </dees-tile>
    `;
  }

  private normalizeIcon(iconArg?: string): string {
    if (!iconArg) return '';
    if (iconArg.includes(':')) return iconArg;
    const legacyIconMap: Record<string, string> = {
      plus: 'lucide:Plus',
      server: 'lucide:Server',
      globe: 'lucide:Globe',
      logs: 'lucide:FileText',
    };
    return legacyIconMap[iconArg] || '';
  }

  private handleActionClick(action: IQuickAction) {
    this.dispatchEvent(
      new CustomEvent('action-click', {
        detail: action,
        bubbles: true,
        composed: true,
      })
    );
  }
}
